"use client";

import React, {createContext, useContext, useEffect, useState} from "react";
import type { ReactNode } from "react";
import axios from "axios";
import { httpClient } from "../api/clients";
import { AUTH } from "../api/endpoints";

interface UserContextType {
	isLoggedIn: boolean;
	showLoginModal: boolean;
	setShowLoginModal: (value: boolean) => void;
	setIsLoggedIn: (value: boolean) => void;
	user: {
		name?: string;
		email?: string;
	} | null;
	setUser: (user: { name?: string; email?: string } | null) => void;
	logout: () => void;
	userType: "homebuyer" | "builder" | null;
	setUserType: (type: "homebuyer" | "builder" | null) => void;
	validateToken: (token: string) => Promise<boolean>;
	clientId: number | null;
	setClientId: (id: number | null) => void;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

export const useUserContext = () => {
	const context = useContext(UserContext);
	if (!context) {
		throw new Error("useUserContext must be used within a UserContextProvider");
	}
	return context;
};

interface UserContextProviderProps {
	children: ReactNode;
}

export function getTokenFromStorage() {
	// Only use cookies
	if (typeof document !== "undefined") {
		const match = document.cookie.match(/(?:^|; )access_token=([^;]*)/);
		if (match) return match[1];
	}
	return null;
}

export function removeTokenFromStorage() {
	if (typeof document !== "undefined") {
		document.cookie = "access_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
	}
}

export function setTokenInStorage(token: string) {
	if (typeof document !== "undefined") {
		document.cookie = `access_token=${token}; path=/;`;
	}
}

export function parseJwt(token: string): { email?: string; name?: string } | null {
	try {
		const base64Url = token.split(".")[1];
		const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
		const jsonPayload = decodeURIComponent(
			atob(base64)
				.split("")
				.map(function (c) {
					return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
				})
				.join("")
		);
		const payload = JSON.parse(jsonPayload);
		return {email: payload.email, name: payload.name};
	} catch {
		return null;
	}
}

export const UserContextProvider = ({children}: UserContextProviderProps) => {
	const [isLoggedIn, setIsLoggedIn] = useState(true);
	const [showLoginModal, setShowLoginModal] = useState(false);
	const [user, setUser] = useState<{ name?: string; email?: string } | null>(null);
	const [userType, setUserType] = useState<"homebuyer" | "builder" | null>("homebuyer");
	const [clientId, setClientId] = useState<number | null>(null);

	// add logout function
	const logout = () => {
		setIsLoggedIn(false);
		setUser(null);
		setUserType(null);
		removeTokenFromStorage();
	};

	useEffect(() => {
		console.log("clientId: ", clientId);
	}, [clientId]);

	// Add validateToken function
	const validateToken = async (token: string): Promise<boolean> => {
		console.log("token", token); 
		try {
			const response = await httpClient.post(AUTH.VALIDATE_TOKEN,
				{},
				{
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${token}`,
					},
				}
			);

			if (response.status === 200) {
				// Token is valid, set it in storage and update user state
				console.log("response: ", response.data);
				setClientId(response.data.user.client_id);
				console.log("clientId: ", clientId);

				setTokenInStorage(token);
				const userInfo = parseJwt(token);

				if (userInfo) {
					setUser(userInfo);
					setIsLoggedIn(true);
					setUserType("builder");
				}
				return true;
			}
			return false;
		} catch (error) {
			if (axios.isAxiosError(error)) {
				// Handle 404 - endpoint doesn't exist, skip validation
				if (error.response?.status === 404) {
					console.warn("Token validation endpoint not found. Assuming token is valid.");
					// Parse the token and set user info without server validation
					const userInfo = parseJwt(token);
					if (userInfo) {
						setTokenInStorage(token);
						setUser(userInfo);
						setIsLoggedIn(true);
						setUserType("builder");
						return true;
					}
					return false;
				}
				// Handle 429 Too Many Requests
				if (error.response?.status === 429) {
					console.warn("Rate limit exceeded. Skipping token validation.");
					// Don't retry, just return false
					return false;
				}
				// Handle other specific errors
				if (error.response?.status === 401 || error.response?.status === 403) {
					// Token is invalid
					return false;
				}
			}
			console.error("Token validation failed:", error);
			return false;
		}
	};

	const value: UserContextType = {
		isLoggedIn,
		setIsLoggedIn,
		user,
		setUser,
		logout,
		userType,
		setUserType,
		validateToken,
		clientId,
		setClientId,
		showLoginModal,
		setShowLoginModal
	};

	return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
